/**
 * @krisspy-file
 * @type page
 * @name "FollowUpEmails"
 * @title "Follow-up Emails"
 * @description "Configure follow-up email templates sent after booking completion"
 * @routes ["/follow-up-emails"]
 * @flowName "communication"
 * @layout "DashboardLayout"
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Save, Loader2, MapPin, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import NumberInput from '../components/NumberInput';
import {
  getFollowUpEmailByLocationAndLanguage,
  getExistingFollowUpEmailLanguages,
  createFollowUpEmail,
  updateFollowUpEmail,
  FollowUpEmail,
  FollowUpEmailInsert
} from '../services/followUpEmails.service';
import { getLocations } from '../services/locations.service';
import { prepareHtmlForQuill, prepareHtmlForStorage } from '../utils/htmlConversion';

// Quill toolbar configuration
const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ 'list': 'bullet' }],
    [{ 'align': [] }],
    ['link'],
    ['clean']
  ]
};

const quillFormats = [
  'bold', 'italic', 'underline',
  'list', 'bullet',
  'align',
  'link'
];

export default function FollowUpEmails() {
  const { t } = useTranslation();
  const { merchantId, locationIds, isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const { defaultLanguage, availableLanguages } = useLanguage();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [locations, setLocations] = useState<Array<{ id: string; location_name: string }>>([]);

  // Track which languages already have templates in the DB
  const [existingLanguages, setExistingLanguages] = useState<string[]>([]);

  // Form data
  const [emailData, setEmailData] = useState<FollowUpEmail | null>(null);
  const [formData, setFormData] = useState({
    subject: '',
    greeting: '',
    maintext: '',
    from_name: '',
    delay_minutes: 1440, // Default: 24 hours (1440 minutes)
    active: true
  });

  // Load locations on mount
  useEffect(() => {
    async function loadLocations() {
      const accessControl = { merchantId, locationIds, isPlatformAdmin };
      const { data } = await getLocations(accessControl, {}, defaultLanguage, defaultLanguage);
      if (data && data.length > 0) {
        setLocations(data);
        // Set default location: if the user is pinned to a single location,
        // default to it; otherwise fall back to the first available location.
        if (locationIds && locationIds.length === 1) {
          setSelectedLocationId(locationIds[0]);
        } else {
          setSelectedLocationId(data[0].id);
        }
      }
      setLoading(false);
    }
    loadLocations();
  }, [merchantId, locationIds, isPlatformAdmin, defaultLanguage]);

  // Load email data when location or language changes
  useEffect(() => {
    if (selectedLocationId) {
      loadEmailData();
      loadExistingLanguages();
    }
  }, [selectedLocationId, selectedLanguage]);

  async function loadExistingLanguages() {
    if (!selectedLocationId) return;
    const { data } = await getExistingFollowUpEmailLanguages(selectedLocationId);
    if (data) setExistingLanguages(data);
  }

  async function loadEmailData() {
    if (!selectedLocationId) return;

    setLoading(true);
    const { data, error } = await getFollowUpEmailByLocationAndLanguage(
      selectedLocationId,
      selectedLanguage
    );

    if (error) {
      console.error('Error loading follow-up email:', error);
    }

    if (data) {
      setEmailData(data);
      setFormData({
        subject: data.subject || '',
        greeting: data.greeting || '',
        maintext: prepareHtmlForQuill(data.maintext),
        from_name: data.from_name || '',
        delay_minutes: data.delay_minutes ?? 1440,
        active: data.active ?? true
      });
    } else {
      setEmailData(null);
      setFormData({
        subject: '',
        greeting: '',
        maintext: '',
        from_name: '',
        delay_minutes: 1440,
        active: true
      });
    }

    setLoading(false);
  }

  async function handleSave() {
    if (!selectedLocationId) {
      showToast(t('followUpEmails.selectLocation'), 'error');
      return;
    }

    if (!formData.subject.trim()) {
      showToast(t('followUpEmails.subjectRequired'), 'error');
      return;
    }

    if (!formData.maintext.trim()) {
      showToast(t('followUpEmails.contentRequired'), 'error');
      return;
    }

    setSaving(true);

    const dataToSave: FollowUpEmailInsert = {
      location_id: selectedLocationId,
      language: selectedLanguage,
      subject: formData.subject,
      greeting: formData.greeting || null,
      maintext: prepareHtmlForStorage(formData.maintext),
      from_name: formData.from_name || null,
      delay_minutes: formData.delay_minutes,
      active: formData.active
    };

    let result;
    if (emailData?.id) {
      result = await updateFollowUpEmail(emailData.id, dataToSave);
    } else {
      result = await createFollowUpEmail(dataToSave);
    }

    if (result.error) {
      showToast(t('followUpEmails.errorSaving') + result.error, 'error');
    } else {
      showToast(t('followUpEmails.savedSuccess'), 'success');
      if (result.data) {
        setEmailData(result.data);
      }
      // Refresh existing languages after creating/updating a template
      loadExistingLanguages();
    }

    setSaving(false);
  }

  // Format delay for display
  function formatDelay(minutes: number): string {
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    }
    return `${hours}h ${remainingMinutes}m`;
  }

  if (loading && locations.length === 0) {
    return (
      <div className="container-centered">
        <Loader2 className="w-8 h-8 animate-spin text-blue" />
        <p className="mt-md text-secondary">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2xl animate-fade-in">
      <div className="flex items-center gap-md mb-xl">
        <button onClick={() => navigate('/dashboard')} className="btn btn-ghost p-xs">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="heading-page">{t('followUpEmails.title')}</h1>
          <p className="text-sm text-secondary">{t('followUpEmails.subtitle')}</p>
        </div>
      </div>

      <div className="space-y-lg">
        {/* Location Selector */}
        {locations.length > 1 && (
          <div className="card mb-lg">
            <div className="card-body">
              <div className="form-group">
                <label className="form-label flex items-center gap-sm">
                  <MapPin className="w-4 h-4" />
                  {t('followUpEmails.location')}
                </label>
                <p className="text-sm text-secondary mb-sm">
                  {t('followUpEmails.locationDesc')}
                </p>
                <select
                  value={selectedLocationId}
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                  className="input w-full max-w-md"
                >
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.location_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Language Selector */}
        {availableLanguages.length > 0 && (
          <div className="card mb-lg">
            <div className="card-body">
              <div className="form-group">
                <label className="form-label">{t('followUpEmails.emailLanguage')}</label>
                <p className="text-sm text-secondary mb-sm">
                  {t('followUpEmails.languageDesc')}
                </p>
                <div className="flex gap-sm mt-sm overflow-x-auto">
                  {availableLanguages.map((lang) => {
                    const hasTemplate = existingLanguages.includes(lang.code);
                    return (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => setSelectedLanguage(lang.code)}
                        className={`px-md py-sm rounded-md flex items-center gap-xs whitespace-nowrap transition-colors ${
                          selectedLanguage === lang.code
                            ? 'bg-blue text-white'
                            : 'bg-secondary text-secondary hover:bg-primary/10'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span className="text-sm font-medium">{lang.label}</span>
                        {lang.code === defaultLanguage && (
                          <span className="text-xs opacity-75 ml-xs">{t('common.default')}</span>
                        )}
                        {!hasTemplate && (
                          <span className={`text-xs px-xs py-px rounded ${
                            selectedLanguage === lang.code
                              ? 'bg-white/20 text-white'
                              : 'bg-warning/20 text-warning'
                          }`}>{t('common.new')}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Email Form */}
        <div className="card">
          <div className="card-header">
            <h2 className="heading-section">{t('followUpEmails.templateTitle')}</h2>
            <p className="text-sm text-secondary mt-xs">
              {t('followUpEmails.templateDesc')}
            </p>
          </div>
          <div className="card-body space-y-lg">
            {/* Active Toggle */}
            <div className="form-group">
              <label className="flex items-center gap-md cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-4 h-4 text-blue border-primary rounded focus:ring-blue"
                />
                <span className="text-sm font-medium text-primary">{t('followUpEmails.enableEmails')}</span>
              </label>
              <p className="text-xs text-secondary mt-xs ml-7">
                {t('followUpEmails.enableDesc')}
              </p>
            </div>

            {/* Delay Setting */}
            <div className="form-group">
              <label className="form-label flex items-center gap-sm">
                <Clock className="w-4 h-4" />
                {t('followUpEmails.sendDelay')}
              </label>
              <div className="flex items-center gap-md">
                <NumberInput
                  value={formData.delay_minutes}
                  onChange={(value) => setFormData({ ...formData, delay_minutes: value || 0 })}
                  min={0}
                  mode="int"
                  className="input w-32"
                />
                <span className="text-sm text-secondary">{t('followUpEmails.minutes')}</span>
                <span className="text-sm text-primary font-medium">
                  ({formatDelay(formData.delay_minutes)})
                </span>
              </div>
              <p className="text-xs text-secondary mt-xs">
                {t('followUpEmails.delayDesc')}
              </p>
            </div>

            {/* From Name */}
            <div className="form-group">
              <label className="form-label">{t('followUpEmails.fromName')}</label>
              <input
                type="text"
                value={formData.from_name}
                onChange={(e) => setFormData({ ...formData, from_name: e.target.value })}
                className="input w-full"
                placeholder={t('followUpEmails.fromNamePlaceholder')}
              />
              <p className="text-xs text-secondary mt-xs">
                {t('followUpEmails.fromNameHint')}
              </p>
            </div>

            {/* Subject */}
            <div className="form-group">
              <label className="form-label">{t('followUpEmails.subject')} <span className="text-danger">*</span></label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="input w-full"
                placeholder={t('followUpEmails.subjectPlaceholder')}
              />
            </div>

            {/* Greeting */}
            <div className="form-group">
              <label className="form-label">{t('followUpEmails.greeting')}</label>
              <input
                type="text"
                value={formData.greeting}
                onChange={(e) => setFormData({ ...formData, greeting: e.target.value })}
                className="input w-full"
                placeholder={t('followUpEmails.greetingPlaceholder')}
              />
              <p className="text-xs text-secondary mt-xs">
                {t('followUpEmails.greetingHint')}
              </p>
            </div>

            {/* Email Body */}
            <div className="form-group">
              <label className="form-label">{t('followUpEmails.emailContent')} <span className="text-danger">*</span></label>
              <div className="quill-wrapper">
                <ReactQuill
                  theme="snow"
                  value={formData.maintext}
                  onChange={(value) => setFormData(prev => ({ ...prev, maintext: value }))}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder={t('followUpEmails.contentPlaceholder')}
                />
              </div>
            </div>

            {/* Save Button */}
            <div className="mt-lg flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving || !selectedLocationId}
                className="btn btn-primary"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('common.saving')}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {t('followUpEmails.saveEmail')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
